import { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsLMS';
import { getSessionUser } from '../../../../../utils/SessionUser';
import AlertDialog from '../../Shared/AlertDialog';
import ConfirmDialog from '../../Shared/ConfirmDialog';

const TITLETAB = 'Asignación de Cursos';

export default function EstudianteCursoTab({ idEstudiante, onClose, onDirtyChange }) {
    const [loading, setLoading] = useState(true);
    const [allCourses, setAllCourses] = useState([]);

    // Stored courses (Inactive + Active) from the new endpoint
    // We use this to know if we should Update(reactivate) or Create
    const [storedCourses, setStoredCourses] = useState([]);

    // Local state for the UI (what is currently assigned in the right column)
    // This should track Active assignments
    const [currentAssigned, setCurrentAssigned] = useState([]);

    const [dragOverColumn, setDragOverColumn] = useState(null);
    const [saving, setSaving] = useState(false);
    const [query, setQuery] = useState('');

    // Estado para el modal de alerta
    const [alertState, setAlertState] = useState({ open: false, title: '', message: '', variant: 'warning' });
    // Estado para el modal de confirmación
    const [confirmState, setConfirmState] = useState({ open: false, title: '', message: '', onConfirm: null });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // 1. Fetch Basic Data
            const [resAll, resStored] = await Promise.all([
                axios.get(ENDPOINTS.cursosAdminHistorico),
                axios.get(ENDPOINTS.estudiantesCursoStored(idEstudiante))
            ]);

            const courses = (resAll?.data || []).map(Models.cursos);
            const stored = (resStored?.data || []).map(Models.estudiantesCurso);

            // 2. Fetch Progress Dependencies (mimicking CourseCurriculumStudent.jsx logic)
            const [respModulos, respLecciones, respModuloContenido, respLeccionEst, respCuestInt, respExamInt] = await Promise.all([
                axios.get(ENDPOINTS.modulo),
                axios.get(ENDPOINTS.lecciones),
                axios.get(ENDPOINTS.moduloContenido), // Need this to link items to modules
                axios.get(ENDPOINTS.leccionEstudiante),
                axios.get(ENDPOINTS.cuestionariosIntentos), // Assuming endpoints exist in ENDPOINTS object, if not use string or import from Utils
                axios.get(ENDPOINTS.examenesIntentos)
            ]);

            // Utils for mapping
            const allModules = respModulos.data || []; // { idModulo, idCurso }
            const allLessons = respLecciones.data || []; // { idLeccion, idModulo }
            const allContent = respModuloContenido.data || []; // { idModulo, idAsociado, tablaContenido }

            // Student Progress Data
            const allLeccionEstData = respLeccionEst.data || [];
            const allCuestIntData = respCuestInt.data || [];
            const allExamIntData = respExamInt.data || [];

            // FILTER progress for THIS student
            const myLessons = allLeccionEstData.filter(x => (x.idEstudiante || x.IdEstudiante) === idEstudiante && (x.idEstado || x.IdEstado) === 2);
            const myQuizzes = allCuestIntData.filter(x => (x.idEstudiante || x.IdEstudiante) === idEstudiante && (x.calificacion || x.Calificacion) >= 60);
            const myExams = allExamIntData.filter(x => (x.idEstudiante || x.IdEstudiante) === idEstudiante && (x.calificacion || x.Calificacion) >= 60);

            // Set of IDs that are completed
            const completedLeccionIds = new Set(myLessons.map(x => x.idLeccion || x.IdLeccion));
            const completedQuizIds = new Set(myQuizzes.map(x => x.idCuestionario || x.IdCuestionario));
            const completedExamIds = new Set(myExams.map(x => x.idExamen || x.IdExamen));

            // Map CourseID -> Boolean (HasProgress)
            const courseHasProgressMap = new Set();

            // Build map: ContentID -> CourseID
            // We iterate modules -> content
            allModules.forEach(mod => {
                const courseId = mod.idCurso || mod.IdCurso;
                if (!courseId) return;

                // Check lessons via legacy relation (Leccion.IdModulo)
                // Or via ModuloContenido
                // Strategy 1: Check Lecciones table
                allLessons.filter(l => (l.idModulo || l.IdModulo) === (mod.idModulo || mod.IdModulo)).forEach(l => {
                    if (completedLeccionIds.has(l.idLeccion || l.IdLeccion)) {
                        courseHasProgressMap.add(courseId);
                    }
                });

                // Strategy 2: Check ModuloContenido (for Quizzes/Exams/Lessons)
                allContent.filter(c => (c.idModulo || c.IdModulo) === (mod.idModulo || mod.IdModulo) && (c.activo || c.Activo)).forEach(c => {
                    const type = c.tablaContenido || c.TablaContenido;
                    const idIdx = c.idAsociado || c.IdAsociado;

                    if (type === 'Lecciones' && completedLeccionIds.has(idIdx)) courseHasProgressMap.add(courseId);
                    if (type === 'Cuestionario' && completedQuizIds.has(idIdx)) courseHasProgressMap.add(courseId);
                    if (type === 'Examen' && completedExamIds.has(idIdx)) courseHasProgressMap.add(courseId);
                });
            });


            setAllCourses(courses);
            setStoredCourses(stored);

            // Initial assigned are those stored with Activo = true
            // We enrich them with course data
            const activeStored = stored.filter(s => s.activo);
            const enrichedActive = activeStored.map(s => {
                const c = courses.find(x => x.idCurso === s.idCurso);
                return {
                    ...s,
                    nombreCurso: c ? c.nombre : `Curso #${s.idCurso}`,
                    duracion: c ? c.duracion : '',
                    costo: c ? c.costo : 0,
                    // Calculated flag
                    tieneProgreso: courseHasProgressMap.has(s.idCurso)
                };
            });

            setCurrentAssigned(enrichedActive);

        } catch (e) {
            console.error(e);
            setAlertState({
                open: true,
                title: 'Error',
                message: 'Error al cargar cursos para el estudiante',
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    }, [idEstudiante]);

    useEffect(() => {
        if (idEstudiante) load();
    }, [idEstudiante, load]);

    const hasChanges = useMemo(() => {
        // Compare currentAssigned vs storedCourses that are active
        const initialActive = storedCourses.filter(s => s.activo);
        const initialIds = new Set(initialActive.map(a => a.idCurso));
        if (initialIds.size !== currentAssigned.length) return true;
        return currentAssigned.some(a => !initialIds.has(a.idCurso));
    }, [storedCourses, currentAssigned]);

    useEffect(() => {
        if (onDirtyChange) {
            onDirtyChange(hasChanges);
        }
    }, [hasChanges, onDirtyChange]);

    // Available Courses: All courses NOT in currentAssigned
    const availableCourses = useMemo(() => {
        const assignedIds = new Set(currentAssigned.map(a => a.idCurso));
        let available = allCourses.filter(c => !assignedIds.has(c.idCurso) && c.activo && c.terminado);

        // Filter by query
        if (query.trim()) {
            const q = query.trim().toLowerCase();
            available = available.filter(c => c.nombre.toLowerCase().includes(q));
        }
        return available;
    }, [allCourses, currentAssigned, query]);

    // --- Drag and Drop Handlers ---
    const handleDragStart = (e, item, source) => {
        e.dataTransfer.setData('application/json', JSON.stringify({ item, source }));
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, targetColumn) => {
        e.preventDefault();
        if (dragOverColumn !== targetColumn) {
            setDragOverColumn(targetColumn);
        }
    };

    const handleDrop = (e, targetColumn) => {
        e.preventDefault();
        setDragOverColumn(null);

        const dataStr = e.dataTransfer.getData('application/json');
        if (!dataStr) return;

        const { item, source } = JSON.parse(dataStr);
        if (source === targetColumn) return;

        if (targetColumn === 'assigned' && source === 'available') {
            addCourseLocal(item); // item is a Course
        } else if (targetColumn === 'available' && source === 'assigned') {
            removeCourseLocal(item); // item is an Assignment (EstudianteCurso)
        }
    };

    // --- Local Modifiers ---
    const addCourseLocal = (courseItem) => {
        // Check if already assigned (shouldn't happen due to filtering, but safe check)
        if (currentAssigned.some(a => a.idCurso === courseItem.idCurso)) return;

        // Check if it exists in stored (inactive) to re-use ID or properties if needed (though on save we re-check)
        const existingStored = storedCourses.find(s => s.idCurso === courseItem.idCurso);

        const newAssignment = {
            idEstudiantesCursos: existingStored ? existingStored.idEstudiantesCursos : 0, // 0 means new to backend logic, but we might handle upsert
            idEstudiante: Number(idEstudiante),
            idCurso: courseItem.idCurso,
            calificacionFinal: existingStored ? existingStored.calificacionFinal : 0,
            idEstado: existingStored ? existingStored.idEstado : 2, // Default 2 (En Curso? or Registered?)
            certificadoGenerado: existingStored ? existingStored.certificadoGenerado : false,
            ultimoAcceso: existingStored ? existingStored.ultimoAcceso : null, // null is new Date().toISOString() in backend
            activo: true,

            // UI Props
            nombreCurso: courseItem.nombre,
            duracion: courseItem.duracion,
        };

        setCurrentAssigned(prev => [...prev, newAssignment]);
    };

    const removeCourseLocal = (assignmentItem) => {
        // Validar si tiene certificado generado
        if (assignmentItem.certificadoGenerado) {
            setAlertState({
                open: true,
                title: 'Acción denegada',
                message: `El curso "${assignmentItem.nombreCurso}" no se puede remover porque ya tiene un certificado generado.`,
                variant: 'warning'
            });
            return;
        }

        // Validar si tiene progreso
        if (assignmentItem.tieneProgreso) {
            setConfirmState({
                open: true,
                title: 'Advertencia',
                message: `El curso "${assignmentItem.nombreCurso}" ya tiene progreso registrado. ¿Estás seguro de que deseas desasignarlo? Esto podría afectar el historial del estudiante.`,
                variant: 'warning',
                onConfirm: () => {
                    setCurrentAssigned(prev => prev.filter(a => a.idCurso !== assignmentItem.idCurso));
                }
            });
            return;
        }

        setCurrentAssigned(prev => prev.filter(a => a.idCurso !== assignmentItem.idCurso));
    };

    // --- Save / Cancel ---
    const handleSave = async () => {
        setSaving(true);
        try {
            const usuarioActual = getSessionUser() || { idUsuario: 1 };
            const promises = [];
            const currentIds = new Set(currentAssigned.map(a => a.idCurso));

            // 1. ITEMS TO BE ACTIVE (In Current Assigned)
            for (const assignment of currentAssigned) {
                const stored = storedCourses.find(s => s.idCurso === assignment.idCurso);

                if (stored) {
                    // Exists in DB (Active or Inactive)
                    if (!stored.activo) {
                        // Was inactive, now active => REACTIVATE
                        promises.push(axios.post(ENDPOINTS.estudiantesCursoReactivate(stored.idEstudiantesCursos), {
                            idUsuarioActual: usuarioActual.idUsuario
                        }));
                    } else {
                        // Already active.
                    }
                } else {
                    // New record => INSERT
                    const payload = {
                        idEstudiante: Number(idEstudiante),
                        idCurso: assignment.idCurso,
                        calificacionFinal: 0,
                        idEstado: 1, // 1 = Inscrito/Activo
                        certificadoGenerado: false,
                        ultimoAcceso: new Date().toISOString(),
                        activo: true,
                        idUsuarioActual: usuarioActual.idUsuario
                    };
                    promises.push(axios.post(ENDPOINTS.estudiantesCurso, payload));
                }
            }

            // 2. ITEMS TO BE INACTIVE (Removed from Current Assigned)
            // "Delete" logic -> User confirmed sp_EstudiantesCurso_Eliminar handles logical delete.
            for (const stored of storedCourses) {
                if (stored.activo && !currentIds.has(stored.idCurso)) {
                    // Was active, now removed => CALL DELETE
                    promises.push(axios.delete(ENDPOINTS.estudiantesCursoById(stored.idEstudiantesCursos)));
                }
            }

            await Promise.all(promises);

            setAlertState({
                open: true,
                title: 'Éxito',
                message: 'Cambios guardados correctamente.',
                variant: 'success'
            });

            await load(); // Reload

        } catch (e) {
            console.error(e);
            setAlertState({
                open: true,
                title: 'Error',
                message: 'Error al guardar cambios.',
                variant: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        const activeStored = storedCourses.filter(s => s.activo);
        const enrichedActive = activeStored.map(s => {
            const c = allCourses.find(x => x.idCurso === s.idCurso);
            // Re-evaluar `tieneProgreso`
            // En caso que queramos evitarlo aqui, idealmente guardamos un mapa limpio en memoria.
            return {
                ...s,
                nombreCurso: c ? c.nombre : `Curso #${s.idCurso}`,
                duracion: c ? c.duracion : '',
                costo: c ? c.costo : 0,
                // Conservaremos falso si es cancelar, no pasará nada o puedes simplificar la variable aquí
                tieneProgreso: currentAssigned.find(a => a.idCurso === s.idCurso)?.tieneProgreso || false
            };
        });
        setCurrentAssigned(enrichedActive);
    };

    return (
        <div className="mini-crud">
            <AlertDialog
                open={alertState.open}
                onClose={() => setAlertState({ ...alertState, open: false })}
                title={alertState.title}
                message={alertState.message}
                variant={alertState.variant}
            />

            <ConfirmDialog
                open={confirmState.open}
                onClose={() => setConfirmState({ ...confirmState, open: false })}
                onConfirm={confirmState.onConfirm}
                title={confirmState.title}
                message={confirmState.message}
                variant="warning"
                confirmText="Sí, desasignar"
                cancelText="Cancelar"
            />

            <div className="mini-topbar">
                <div>
                    <h3 className="roles-title" style={{ margin: 0 }}>{TITLETAB}</h3>
                    <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                        Arrastra los cursos para asignar o desasignar.
                    </div>
                </div>
            </div>


            <div style={{ padding: '0 0.5rem 1rem 0.5rem' }}>
                <input
                    className="roles-search"
                    placeholder="Buscar curso disponible..."
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    style={{ width: '100%', marginBottom: 0 }}
                />
            </div>

            {loading ? (
                <div style={{ padding: '100px 40px', textAlign: 'center', minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="catalog-loading">Cargando cursos del estudiante...</div>
                </div>
            ) : (
                <div className="dnd-container">
                    {/* Left: Available */}
                    <div
                        className={`dnd-column ${dragOverColumn === 'available' ? 'drag-over' : ''}`}
                        onDragOver={(e) => handleDragOver(e, 'available')}
                        onDrop={(e) => handleDrop(e, 'available')}
                    >
                        <div className="dnd-header">Cursos Disponibles</div>
                        {availableCourses.length === 0 ? (
                            <div className="dnd-empty">No hay cursos disponibles</div>
                        ) : (
                            availableCourses.map(course => (
                                <div
                                    key={course.idCurso}
                                    className="dnd-item"
                                    draggable={!saving}
                                    onDragStart={(e) => handleDragStart(e, course, 'available')}
                                >
                                    <span style={{ color: course.activo ? 'inherit' : '#94a3b8' }}>{course.nombre}{!course.activo && ' (Desactivado)'}</span>
                                    <button
                                        type="button"
                                        onClick={() => addCourseLocal(course)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            fontSize: '1.2em',
                                            color: '#28a745',
                                            cursor: 'pointer',
                                            padding: '0 5px'
                                        }}
                                        title="Asignar"
                                    >＋</button>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Right: Assigned */}
                    <div
                        className={`dnd-column ${dragOverColumn === 'assigned' ? 'drag-over' : ''}`}
                        onDragOver={(e) => handleDragOver(e, 'assigned')}
                        onDrop={(e) => handleDrop(e, 'assigned')}
                    >
                        <div className="dnd-header">Cursos Asignados</div>
                        {currentAssigned.length === 0 ? (
                            <div className="dnd-empty">Arrastra cursos aquí</div>
                        ) : (
                            currentAssigned.map(assign => (
                                <div
                                    key={assign.idCurso}
                                    className="dnd-item"
                                    draggable={!saving}
                                    onDragStart={(e) => handleDragStart(e, assign, 'assigned')}
                                >
                                    <span style={{ color: assign.certificadoGenerado ? '#94a3b8' : 'inherit' }}>
                                        {assign.nombreCourse || assign.nombreCurso}
                                        {!(allCourses.find(c => c.idCurso === assign.idCurso)?.activo ?? true) && ' (Desactivado)'}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => removeCourseLocal(assign)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            fontSize: '1.2em',
                                            color: '#dc3545',
                                            cursor: 'pointer',
                                            padding: '0 5px'
                                        }}
                                        title="Desasignar"
                                    >×</button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}


            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1rem' }}>
                <button className="btn-cancel" onClick={() => { handleCancel(); onClose?.(); }} disabled={!hasChanges && saving} type="button">Cerrar / Descartar</button>
                <button
                    className="btn-action"
                    onClick={handleSave}
                    disabled={!hasChanges || saving}
                    style={{ opacity: (!hasChanges || saving) ? 0.5 : 1, cursor: (!hasChanges || saving) ? 'not-allowed' : 'pointer' }}
                    type="button"
                >
                    {saving ? 'Guardando...' : 'Guardar Cambios'}
                </button>
            </div>
        </div>
    );
}
